'use client';

import { Button, Text, VStack } from '@chakra-ui/react';
import { ArrowUpRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';

const STARTERS = [
  'What should we prioritize this week?',
  'How can we make our positioning clearer?',
  'What content would help our ideal customers?',
  'Help me plan our next growth experiment.',
];

export function WorkspaceQuestionSuggestions({
  user,
  projectId,
  onSelect,
  disabled,
}: {
  user: User | null;
  projectId: string;
  onSelect: (question: string) => void;
  disabled: boolean;
}) {
  const scope = `${user?.uid}:${projectId}`;
  const [available, setAvailable] = useState<{
    scope: string;
    analytics: boolean;
    search: boolean;
    intelligence: boolean;
  }>({ scope: '', analytics: false, search: false, intelligence: false });
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const token = await user.getIdToken();
        const read = async (url: string) => {
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          if (!response.ok) return null;
          return response.json();
        };
        const [analytics, intelligence] = await Promise.allSettled([
          read(`/api/integrations/analytics/status?projectId=${encodeURIComponent(projectId)}`),
          read(`/api/projects/${encodeURIComponent(projectId)}/intelligence/status`),
        ]);
        if (controller.signal.aborted) return;
        const sources: Array<{ provider: string; status: string; lastSyncAt?: string }> =
          analytics.status === 'fulfilled' && Array.isArray(analytics.value?.sources)
            ? analytics.value.sources
            : [];
        const has = (providers: string[]) =>
          sources.some(
            source =>
              source.status === 'connected' &&
              Boolean(source.lastSyncAt) &&
              providers.includes(source.provider)
          );
        setAvailable({
          scope,
          analytics: has(['ga4']),
          search: has(['gsc', 'bing_webmaster']),
          intelligence:
            intelligence.status === 'fulfilled' &&
            intelligence.value?.status === 'ready' &&
            Boolean(intelligence.value?.workflowId) &&
            Number(intelligence.value?.promptCount) > 0,
        });
      } catch {
        // General planning prompts remain useful when source availability is unknown.
      }
    })();
    return () => controller.abort();
  }, [user, projectId, scope]);
  const current = available.scope === scope ? available : null;
  const questions = [
    ...(current?.analytics || current?.search || current?.intelligence
      ? ['What changed in our performance this week?']
      : []),
    ...(current?.intelligence ? ['Where are competitors outperforming us?'] : []),
    ...(current?.search || current?.analytics ? ['Which pages should we improve first?'] : []),
    ...(current?.analytics ? ['What should we prioritize to grow revenue?'] : []),
    ...STARTERS,
  ].slice(0, 4);
  return (
    <VStack
      as="nav"
      aria-label="Suggested questions"
      align="stretch"
      gap="1"
      mt="7"
      w="full"
      maxW="440px"
    >
      {questions.map(question => (
        <Button
          key={question}
          variant="ghost"
          justifyContent="space-between"
          gap="4"
          h="auto"
          minH="42px"
          px="3"
          py="2.5"
          borderRadius="md"
          whiteSpace="normal"
          textAlign="left"
          fontWeight="400"
          color="ui.text.secondary"
          disabled={disabled}
          onClick={() => onSelect(question)}
          _hover={{ bg: 'ui.surface.rowHover', color: 'ui.text.primary' }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'ui.border.accent' }}
        >
          <Text as="span" fontSize="sm" lineHeight="1.5">
            {question}
          </Text>
          <ArrowUpRight size={14} aria-hidden="true" style={{ flexShrink: 0, opacity: 0.55 }} />
        </Button>
      ))}
    </VStack>
  );
}
